import qs from 'query-string';
import React, { Suspense } from 'react';
import { Route, RouteProps, useLocation } from 'react-router-dom';

// @mui
import Box from '@mui/material/Box';
// @ld
import { Loader, config } from '@ld/core';
import { publicOrgLogoUrl } from '@ld/utils';

// @custom
import Logo from '../logo';
import Footer from '../footer';
import useRedirectToApp from './use-redirect-to-app';
import {
  AuthContainer,
  ShapesBackground,
  Main,
  Card,
  PoweredByLink,
  LinkCard
} from './styles';

// Logo URL is deterministic from the slug — no Firestore lookup needed
// pre-auth. If the GCS file is missing (slug garbage, or branded org with
// no logo yet), <img onError> falls back to the default Lifedocs logo.
// "Garbage slug" and "valid slug, no logo" look identical → no enumeration
// signal.
const BrandedLogo = ({ slug }: { slug: string }) => {
  const [broken, setBroken] = React.useState(false);
  if (broken) {
    return <Logo height={50} />;
  }
  return (
    <img
      src={publicOrgLogoUrl(slug)}
      alt='Organisation Logo'
      width={180}
      onError={() => setBroken(true)}
    />
  );
};

const AuthLayout = ({ children }: { children: React.ReactNode }) => {
  const { search } = useLocation();
  const { organisation } = qs.parse(search);
  const slug = typeof organisation === 'string' ? organisation : undefined;
  const branded = Boolean(slug);

  return (
    <AuthContainer>
      <Main $isCentered>
        {slug ? <BrandedLogo slug={slug} /> : <Logo height={50} />}
        <Card>{children}</Card>
      </Main>
      {branded ? (
        <LinkCard>
          <PoweredByLink href={config.websiteUrl} target='_blank'>
            Powered by
            <Logo width={120} height={15} />
          </PoweredByLink>
        </LinkCard>
      ) : (
        <Footer />
      )}
      <ShapesBackground />
    </AuthContainer>
  );
};

const Fallback = () => (
  <Box display='flex' alignItems='center' justifyContent='center' p={3}>
    <Loader />
  </Box>
);

const AuthLayoutRoute = ({
  component: Component,
  children,
  path,
  ...rest
}: RouteProps & { children?: React.ReactNode }) => {
  useRedirectToApp();
  return (
    <Route
      {...rest}
      render={(props) => (
        <AuthLayout>
          <Suspense fallback={<Fallback />}>
            {Component ? <Component {...props} /> : children}
          </Suspense>
        </AuthLayout>
      )}
    />
  );
};

export default AuthLayoutRoute;
